import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, UserCheck, Package, ShoppingCart, Briefcase, LineChart, Bell, Sparkles, Crown, Menu, Mail, Contact, Tv, Megaphone, Gavel, Award, LogOut, Tag, Upload, Store, FileText } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import api from "../lib/api";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard", end: true },
  { to: "/admin/approvals", label: "Verifica B2B", icon: UserCheck, testid: "admin-nav-approvals" },
  { to: "/admin/customers", label: "Clienti", icon: Contact, testid: "admin-nav-customers" },
  { to: "/admin/brokers", label: "Broker", icon: Briefcase, testid: "admin-nav-brokers" },
  { to: "/admin/users", label: "Tutti gli utenti", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/catalog", label: "Catalogo", icon: Package, testid: "admin-nav-catalog" },
  { to: "/admin/orders", label: "Ordini", icon: ShoppingCart, testid: "admin-nav-orders" },
  { to: "/admin/club", label: "Club Privilegia", icon: Crown, testid: "admin-nav-club" },
  { to: "/admin/auctions", label: "Aste", icon: Gavel, testid: "admin-nav-auctions", badgeKey: "auctions_pending" },
  { to: "/admin/b2b-lots", label: "Lotti B2B", icon: Tag, testid: "admin-nav-b2b-lots", badgeKey: "lots_pending" },
  { to: "/admin/vendor-services", label: "Servizi Fornitori", icon: Store, testid: "admin-nav-vendor-services", badgeKey: "vendor_services_new" },
  { to: "/admin/vendor-documents", label: "Documenti Aziende", icon: FileText, testid: "admin-nav-vendor-documents" },
  { to: "/admin/imports", label: "Import & Documenti", icon: Upload, testid: "admin-nav-imports" },
  { to: "/admin/franchising", label: "Franchising", icon: Award, testid: "admin-nav-franchising", badgeKey: "franchising_pending" },
  { to: "/admin/marketing", label: "Marketing Hub", icon: Megaphone, testid: "admin-nav-marketing" },
  { to: "/admin/media", label: "Amazonico TV · Media", icon: Tv, testid: "admin-nav-media" },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail, testid: "admin-nav-newsletter" },
  { to: "/admin/crm", label: "CRM Leads", icon: LineChart, testid: "admin-nav-crm" },
  { to: "/admin/alerts", label: "Alerts", icon: Bell, testid: "admin-nav-alerts" },
  { to: "/admin/analyst", label: "AI Analyst", icon: Sparkles, testid: "admin-nav-analyst" },
];

function useBadges() {
  const [badges, setBadges] = useState({ auctions_pending: 0, franchising_pending: 0, lots_pending: 0, vendor_services_new: 0 });
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const [a, f, l, v] = await Promise.all([
          api.get("/admin/auction-proposals/pending-count").catch(() => ({ data: { pending: 0 } })),
          api.get("/admin/franchising/requests/pending-count").catch(() => ({ data: { pending: 0 } })),
          api.get("/admin/b2b/lots/pending-count").catch(() => ({ data: { pending: 0 } })),
          api.get("/admin/vendor/services?status=new").catch(() => ({ data: { total: 0 } })),
        ]);
        setBadges({
          auctions_pending: a.data?.pending || 0,
          franchising_pending: f.data?.pending || 0,
          lots_pending: l.data?.pending || 0,
          vendor_services_new: v.data?.total || 0,
        });
      } catch { /* silent */ }
    };
    fetchBadges();
    const t = setInterval(fetchBadges, 30000);
    return () => clearInterval(t);
  }, []);
  return badges;
}

function NavItems({ onItemClick, testidPrefix = "", badges }) {
  return items.map((i) => {
    const badgeCount = i.badgeKey ? (badges?.[i.badgeKey] || 0) : 0;
    return (
      <NavLink
        key={i.to}
        to={i.to}
        end={i.end}
        data-testid={`${testidPrefix}${i.testid}`}
        onClick={onItemClick}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
            isActive ? "bg-[hsl(var(--primary))] text-white" : "text-foreground hover:bg-[hsl(var(--muted))]"
          }`
        }
      >
        <i.icon className="h-4 w-4" />
        <span className="flex-1">{i.label}</span>
        {badgeCount > 0 && (
          <span data-testid={`badge-${i.testid}`} className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-red-500 text-white rounded-full">{badgeCount}</span>
        )}
      </NavLink>
    );
  });
}

function CurrentLabel() {
  const loc = useLocation();
  const exact = items.find((i) => i.end ? loc.pathname === i.to : loc.pathname.startsWith(i.to));
  return exact?.label || "Admin";
}

export default function AdminSidebar() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const badges = useBadges();

  return (
    <>
      {/* DESKTOP SIDEBAR */}
      <aside
        data-testid="admin-sidebar"
        className="fixed left-0 top-0 h-screen w-[260px] bg-[hsl(var(--card))] border-r border-[hsl(var(--border))] p-5 hidden lg:flex flex-col"
      >
        <Link to="/" data-testid="admin-logo" className="mb-6 flex items-center gap-3 shrink-0">
          <img src="/brand/logo.png" alt="Amazonico 3000" className="h-14 w-auto" />
          <div className="text-[9px] uppercase tracking-[0.4em] text-[hsl(var(--accent))]">Super Admin</div>
        </Link>
        <nav className="flex flex-col gap-1 overflow-y-auto flex-1 pr-1 -mr-1"><NavItems badges={badges} /></nav>
        <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] shrink-0">
          <div className="text-xs text-muted-foreground mb-1">Connesso come</div>
          <div className="text-sm font-semibold truncate">{user?.name}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--accent))] mb-3">{user?.role}</div>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold rounded-sm border border-[hsl(var(--border))] text-foreground hover:bg-destructive hover:text-white hover:border-destructive transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Esci dall'account
          </button>
        </div>
      </aside>

      {/* MOBILE TOPBAR */}
      <header data-testid="admin-mobile-topbar" className="lg:hidden sticky top-0 z-40 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
        <div className="px-4 h-14 flex items-center justify-between gap-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="admin-mobile-menu-btn" className="p-2 rounded-sm hover:bg-[hsl(var(--muted))]" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-5 flex flex-col">
              <VisuallyHidden><SheetTitle>Menu Admin</SheetTitle></VisuallyHidden>
              <Link to="/" onClick={() => setMobileOpen(false)} className="mb-6 flex items-center gap-3 shrink-0">
                <img src="/brand/logo.png" alt="Amazonico 3000" className="h-12 w-auto" />
                <div className="text-[9px] uppercase tracking-[0.4em] text-[hsl(var(--accent))]">Super Admin</div>
              </Link>
              <nav className="flex flex-col gap-1 overflow-y-auto flex-1 pr-1 -mr-1"><NavItems testidPrefix="admin-mobile-" onItemClick={() => setMobileOpen(false)} badges={badges} /></nav>
              <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] shrink-0">
                <div className="text-xs text-muted-foreground mb-1">Connesso come</div>
                <div className="text-sm font-semibold truncate">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--accent))] mb-3">{user?.role}</div>
                <button
                  data-testid="admin-mobile-logout"
                  onClick={() => { setMobileOpen(false); logout(); }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold rounded-sm border border-[hsl(var(--border))] text-foreground hover:bg-destructive hover:text-white hover:border-destructive transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Esci dall'account
                </button>
              </div>
            </SheetContent>
          </Sheet>
          <Link to="/" className="flex items-center gap-2">
            <img src="/brand/logo.png" alt="" className="h-8 w-auto" />
            <span className="text-[9px] uppercase tracking-[0.3em] text-[hsl(var(--accent))]">Admin</span>
          </Link>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground truncate max-w-[40vw]">
            <CurrentLabel />
          </div>
        </div>
      </header>
    </>
  );
}
